// src/components/LegalPageShell.js
//
// Shared layout for the public-facing legal + support pages
// (PrivacyPage, TermsPage, CookiePolicyPage, SupportPage).
//
// Replaces ~1,500 lines of duplicate chrome across those four files —
// each one used to render its own copy of nav + footer + prose styles
// (and a separate mobile branch on top of that). Now each page just
// provides a title and its content; everything else lives here.
//
// Uses the app's design tokens (var(--color-bg), var(--color-card),
// rgb(var(--accent-primary))) so the legal pages match the redesigned
// HomePage and follow the user's accent + theme.

import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Menu, X, ShieldCheck } from 'lucide-react';
import { AnimatePresence, motion } from 'framer-motion';
import { useIsMobile } from '../hooks/useIsMobile';

/**
 * Marketing-facing nav. Mirrors HomePage.renderNav so navigating between
 * /, /privacy, /terms, etc. feels like one continuous shell instead of
 * jumping between separately-styled microsites.
 */
function LegalNav() {
  const isMobile = useIsMobile();
  const [mobileOpen, setMobileOpen] = useState(false);

  return (
    <nav
      className="sticky top-0 z-50 backdrop-blur-md border-b"
      style={{
        backgroundColor: 'color-mix(in srgb, var(--color-bg) 85%, transparent)',
        borderColor: 'var(--color-border)',
      }}
    >
      <div className="max-w-[1280px] mx-auto px-4 sm:px-6 py-3 flex items-center justify-between">
        {/* Logo + wordmark — matches HomePage nav. Real brand mark from
            /public paired with the letter-spaced NEXUS caps used on the
            share-card modals. */}
        <Link to="/" className="flex items-center gap-2" aria-label="Nexus home">
          <img
            src="/nexus-logo-only2.png"
            alt=""
            className="w-7 h-7 object-contain"
          />
          <span
            className="text-sm sm:text-base font-medium tracking-widest"
            style={{ color: 'var(--color-text)' }}
          >
            Nexus
          </span>
        </Link>

        {!isMobile && (
          <div className="flex items-center gap-6 text-sm" style={{ color: 'var(--color-text-muted)' }}>
            <Link to="/#features" className="hover:opacity-80 transition-opacity" style={{ color: 'inherit' }}>Features</Link>
            <Link to="/#pricing" className="hover:opacity-80 transition-opacity" style={{ color: 'inherit' }}>Pricing</Link>
            <Link to="/#faq" className="hover:opacity-80 transition-opacity" style={{ color: 'inherit' }}>FAQ</Link>
            <Link to="/support" className="hover:opacity-80 transition-opacity" style={{ color: 'inherit' }}>Support</Link>
          </div>
        )}

        <div className="flex items-center gap-2">
          {!isMobile && (
            <Link
              to="/?auth=login"
              className="px-3 py-1.5 text-sm rounded-md font-medium transition-opacity hover:opacity-80"
              style={{ color: 'var(--color-text-muted)' }}
            >
              Sign in
            </Link>
          )}
          <Link
            to="/?auth=signup"
            className="px-3 py-1.5 text-sm rounded-md font-medium text-white transition-opacity hover:opacity-90"
            style={{ backgroundColor: 'rgb(var(--accent-primary))' }}
          >
            Get Started
          </Link>
          {isMobile && (
            <button
              onClick={() => setMobileOpen((v) => !v)}
              className="p-1.5 rounded-md"
              style={{ color: 'var(--color-text-muted)' }}
              aria-label="Toggle menu"
            >
              {mobileOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
            </button>
          )}
        </div>
      </div>

      <AnimatePresence>
        {isMobile && mobileOpen && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="overflow-hidden border-t"
            style={{ borderColor: 'var(--color-border)' }}
          >
            <div className="px-6 py-3 space-y-2 text-sm">
              <Link to="/#features" onClick={() => setMobileOpen(false)} className="block py-1.5" style={{ color: 'var(--color-text-muted)' }}>Features</Link>
              <Link to="/#pricing" onClick={() => setMobileOpen(false)} className="block py-1.5" style={{ color: 'var(--color-text-muted)' }}>Pricing</Link>
              <Link to="/#faq" onClick={() => setMobileOpen(false)} className="block py-1.5" style={{ color: 'var(--color-text-muted)' }}>FAQ</Link>
              <Link to="/support" onClick={() => setMobileOpen(false)} className="block py-1.5" style={{ color: 'var(--color-text-muted)' }}>Support</Link>
              <Link to="/?auth=login" onClick={() => setMobileOpen(false)} className="block py-1.5" style={{ color: 'var(--color-text-muted)' }}>Sign in</Link>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </nav>
  );
}

/**
 * Footer. Slimmer than HomePage's 4-column footer — legal pages don't
 * need the brand pitch repeated. Single row of links + copyright.
 */
function LegalFooter() {
  return (
    <footer
      className="px-4 sm:px-6 py-8 border-t mt-auto"
      style={{ borderColor: 'var(--color-border)' }}
    >
      <div
        className="max-w-[1280px] mx-auto flex flex-col sm:flex-row items-center justify-between gap-4 text-xs"
        style={{ color: 'var(--color-text-muted)' }}
      >
        <div className="flex items-center gap-2">
          <img src="/nexus-logo-only2.png" alt="" className="w-5 h-5 object-contain" />
          <span
            className="text-[11px] font-medium tracking-widest"
            style={{ color: 'var(--color-text)' }}
          >
            Nexus
          </span>
          <span className="opacity-60">·</span>
          <span>© {new Date().getFullYear()} All rights reserved.</span>
        </div>

        <div className="flex items-center gap-5">
          <Link to="/privacy" className="hover:opacity-80 transition-opacity">Privacy</Link>
          <Link to="/terms" className="hover:opacity-80 transition-opacity">Terms</Link>
          <Link to="/cookies" className="hover:opacity-80 transition-opacity">Cookies</Link>
          <Link to="/support" className="hover:opacity-80 transition-opacity">Support</Link>
        </div>

        <span className="hidden sm:flex items-center gap-1.5">
          <ShieldCheck className="w-3.5 h-3.5" />
          SSL encrypted
        </span>
      </div>
    </footer>
  );
}

/**
 * The exported shell. Pages drop their `children` (prose content) inside.
 *
 * Props:
 *   - title:    Page heading shown in the title block
 *   - updated:  Optional "Last updated:" date string (legal pages)
 *   - subtitle: Optional alternative subtitle (used by SupportPage)
 *   - maxWidth: Container max-width in pixels (default 760 — comfortable
 *               reading width for prose; SupportPage overrides to 1024)
 *   - prose:    When true (default), applies the prose styles to children
 *               (h2/h3/p/ul/li/strong typography for legal pages).
 *               Pages with their own card/grid layout pass prose={false}.
 */
export default function LegalPageShell({
  title,
  updated,
  subtitle,
  maxWidth = 760,
  prose = true,
  children,
}) {
  return (
    <div
      className="min-h-screen flex flex-col"
      style={{ backgroundColor: 'var(--color-bg)', color: 'var(--color-text)', fontFamily: "'Inter', sans-serif" }}
    >
      <LegalNav />

      {/* Ambient accent glow behind the title block — same vibe as the
          HomePage hero but much smaller. Adds visual interest without
          competing with the content. */}
      <div className="relative">
        <div
          className="absolute top-0 left-1/2 -translate-x-1/2 w-[500px] h-[200px] pointer-events-none"
          style={{
            background: 'radial-gradient(ellipse at center, rgb(var(--accent-primary) / 0.10) 0%, transparent 70%)',
            filter: 'blur(40px)',
          }}
        />

        <main className="relative flex-1 px-4 sm:px-6 pt-10 sm:pt-14 pb-16">
          <div className="mx-auto" style={{ maxWidth: `${maxWidth}px` }}>
            {/* Title block */}
            <div className="mb-8 sm:mb-10">
              <h1
                className="text-3xl sm:text-4xl font-bold tracking-tight mb-2"
                style={{ color: 'var(--color-text)' }}
              >
                {title}
              </h1>
              {updated && (
                <p className="text-xs sm:text-sm" style={{ color: 'var(--color-text-muted)' }}>
                  Last updated: {updated}
                </p>
              )}
              {subtitle && (
                <p className="text-base sm:text-lg mt-1" style={{ color: 'var(--color-text-muted)' }}>
                  {subtitle}
                </p>
              )}
            </div>

            {/* Content */}
            {prose ? (
              <div className="legal-prose">
                <style>{`
                  .legal-prose h2 {
                    font-size: 1.25rem;
                    font-weight: 600;
                    color: var(--color-text);
                    margin-top: 2rem;
                    margin-bottom: 0.75rem;
                    letter-spacing: -0.01em;
                  }
                  .legal-prose h2:first-of-type {
                    margin-top: 1rem;
                  }
                  .legal-prose h3 {
                    font-size: 1rem;
                    font-weight: 600;
                    color: var(--color-text);
                    margin-top: 1.5rem;
                    margin-bottom: 0.5rem;
                  }
                  .legal-prose p {
                    color: var(--color-text-muted);
                    line-height: 1.7;
                    margin-bottom: 0.9rem;
                    font-size: 0.95rem;
                  }
                  .legal-prose .intro-text {
                    font-size: 1rem;
                    color: var(--color-text-muted);
                    margin-bottom: 1.5rem;
                    padding-bottom: 1.5rem;
                    border-bottom: 1px solid var(--color-border);
                  }
                  /* Callout block for the "IMPORTANT" disclaimer paragraph
                     in the Terms page. Subtle accent-tinted box. */
                  .legal-prose .important-notice {
                    background-color: color-mix(in srgb, rgb(var(--accent-primary)) 6%, transparent);
                    border-left: 3px solid rgb(var(--accent-primary));
                    padding: 0.85rem 1rem;
                    border-radius: 6px;
                    margin: 1.25rem 0;
                  }
                  .legal-prose ul {
                    color: var(--color-text-muted);
                    margin-left: 1.25rem;
                    margin-bottom: 1rem;
                    font-size: 0.95rem;
                    list-style: disc;
                  }
                  .legal-prose li {
                    margin-bottom: 0.35rem;
                    line-height: 1.6;
                  }
                  .legal-prose strong {
                    color: var(--color-text);
                    font-weight: 600;
                  }
                  .legal-prose a {
                    color: rgb(var(--accent-primary));
                    text-decoration: underline;
                    text-decoration-color: color-mix(in srgb, rgb(var(--accent-primary)) 40%, transparent);
                    text-underline-offset: 2px;
                  }
                  .legal-prose a:hover {
                    text-decoration-color: rgb(var(--accent-primary));
                  }
                `}</style>
                {children}
              </div>
            ) : (
              children
            )}
          </div>
        </main>
      </div>

      <LegalFooter />
    </div>
  );
}
